"use client";

// WO-GF-001-P1B-S6 (W7 / X1) — SITE-WIDE CAROUSEL RESTORE. Document-scoped hydrator, same family as
// PopupLayer / DatePickerLayer / ScheduleMapLayer / MarketplaceCartLayer / HubChromeLayer. It drives the
// CAPTURED react-multi-carousel markup IN PLACE — the served mirror IS the library's own rendered output
// (identical `.react-multi-carousel-list` / `-track` / `-item` / `__arrow` / `-dot-list` classes + the
// inlined library CSS `overflow:hidden; position:relative`), so operating that DOM is maximally faithful in
// appearance while restoring the stripped interactivity (R6-6). It does NOT re-mount the react-multi-carousel
// React component (which would require reconstructing every carousel's BE-baked cards/anchors as props and
// replacing the mirror subtree — mutating the served anchor set and the masked pixel region for no visual
// gain). LIB DECISION (S6 close): rehydrate the same lib's own markup in place, EXTEND, no new dependency.
//
// ── S35 · F236 — SPONSOR-LOGO CAROUSEL DOES NOT ROTATE. ROOT-CAUSED, NOT FIXED (budget). ───────────
// Founder walk 2026-07-27: "it must cycle through ALL the partner logos, not sit static on the first
// set." MEASURED on the built home at 1440x900, twice, 3s apart:
//   sponsor track  animationName = none · animationDuration = 0s · transform = none
//   first logo     getBoundingClientRect().x = -2248 at T0 AND at T3 — frozen, and frozen OFF-SCREEN
//                  LEFT, i.e. the capture caught the library mid-slide and it never resumed
//   markup         .react-multi-carousel-item — live's library DOM, JS stripped (the R6-6 photocopy)
//   content        18 <img>, 6 DISTINCT logos x3 (macs · fox · arson · larabar · universal · comor) —
//                  the classic duplicated marquee track. THE LOGOS ARE ALL SERVED AND REAL; nothing
//                  needs inventing, they are simply parked.
// ⚠ THE TRAP FOR WHOEVER GATES THIS: asserting the img `src` list changes over time CANNOT WORK and
// will report a false green-or-red — a marquee TRANSLATES a fixed track, it never swaps srcs. I wrote
// that assertion first and it said "no movement" for a reason unrelated to the defect. Gate the
// TRANSFORM / bounding-rect x of the track, and assert every one of the 6 distinct logos becomes
// visible across a full cycle.
// SIZED: this layer already rehydrates the finite/arrow shapes in place; what is missing is the
// AUTOPLAY-MARQUEE shape. One shape handler here plus a motion gate — not a new dependency, not a
// re-mount of the library component (see the LIB DECISION below, which still stands).
//
// §0f (production snowbus.com, frozen in each page's mirror capture) — THREE captured shapes:
//   • FINITE card carousels — arrows + dots, captured at rest (transform 0, first item active). Dots page by
//     `slidesToShow`; arrows step one item. (home / hub / route / profile.)
//   • INFINITE photo carousel (operator profile, F58) — react-multi-carousel infinite mode: the DOM carries
//     symmetric CLONES around the real slides (e.g. 7 items = 2 lead-clones + 3 real + 2 trail-clones, dots=3
//     = the real slides). Captured mid-cycle (transform ≠ 0). Dots map 1:1 to real slides; navigation is
//     CLAMPED to the real window so a click never lands on a cloned/blank frame.
//   • NO-CONTROL auto-play strips — the client-logo marquee + the 3-photo supplier slideshows (no arrows,
//     no dots), captured mid-rotation. Only auto-play moves them.
//
// HARD GATES honoured:
//   • PIXEL-NEUTRAL AT REST — nothing is moved or reclassed until the user interacts. The captured track
//     transform (a mid-cycle value for auto-play/infinite carousels, NOT 0) is preserved verbatim; the layer
//     seeds its logical position FROM that captured transform so the first interaction continues from the
//     visible slide. Only invisible hooks are added at rest (a data-* attribute + hover-only cursor). (The
//     carousel region is also masked in the gate, so this stays byte-exact.)
//   • AUTO-ROTATE HELD for gate determinism — never auto-advances under the harness. Auto-play is opt-in
//     behind a flag (`window.__carouselAutoplay === true`), OFF by default. Fidelity delta vs live auto-slide
//     (and vs live's infinite WRAP, which the build CLAMPS to real slides) is recorded in the S6 ledger.
//   • LINK-GRAPH SAFE — the layer never clones/injects slides, so the served anchor set is byte-identical to
//     the capture (the clones are already in the captured DOM); the S6 link-graph re-run confirms dangling=0.
//
// Desktop only (mounted by page.tsx on desktop; mobile mirror stays dead — F30 / Phase-2).
// Harness hooks: data-behavior="carousel" + data-carousel-index="N" on each hydrated list.

import { useEffect } from "react";

// react-multi-carousel's default slide animation (matches the captured `transition: transform 400ms ease-in-out`).
const EASE = "transform 400ms ease-in-out";
/** S35 · F236 — sponsor-marquee speed. Calm enough to read a logo, fast enough to be obviously moving. */
const MARQUEE_PX_PER_S = 40;

interface Ctl {
  list: HTMLElement;
  track: HTMLElement;
  items: HTMLElement[];
  dots: HTMLElement[];
  infinite: boolean;
  realStart: number; // DOM index of the first REAL slide (0 for finite; lead-clone count for infinite)
  slidesToShow: number;
  maxPos: number; // last valid logical position
  pos: number; // current logical position (finite: left-edge item index; infinite: real-slide index)
  // RW-16 (S10) — the boundary arrows, so their edge-states can track live's.
  arrowL: HTMLElement | null;
  arrowR: HTMLElement | null;
}

// translate item at DOM index `i` to where item[0] sits (finite align-left; handles variable widths + margins).
function offsetFor(items: HTMLElement[], i: number): number {
  const base = items[0]?.offsetLeft ?? 0;
  const at = items[Math.max(0, Math.min(i, items.length - 1))]?.offsetLeft ?? base;
  return -(at - base);
}

// nearest DOM item index to a captured translateX (seeds pos so interaction continues from the visible slide).
function nearestItem(items: HTMLElement[], translateX: number): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < items.length; i++) {
    const d = Math.abs(offsetFor(items, i) - translateX);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function parseTranslateX(transform: string): number {
  const m = /translate3d\(\s*(-?[0-9.]+)px/.exec(transform || "");
  return m ? Number(m[1]) : 0;
}

// DOM item index for a logical position.
function itemIndexForPos(c: Ctl, pos: number): number {
  return c.infinite ? c.realStart + pos : pos;
}

// which dot is active for the current logical position.
function activeDotForPos(c: Ctl, pos: number): number {
  if (c.dots.length <= 1) return 0;
  if (c.infinite) return Math.min(pos, c.dots.length - 1);
  return c.maxPos <= 0 ? 0 : Math.round((pos / c.maxPos) * (c.dots.length - 1));
}

// logical position for a clicked dot.
function posForDot(c: Ctl, dot: number): number {
  if (c.infinite) return Math.min(dot, c.maxPos);
  if (c.dots.length <= 1 || c.maxPos <= 0) return 0;
  return Math.min(c.maxPos, Math.max(0, Math.round((dot / (c.dots.length - 1)) * c.maxPos)));
}

function apply(c: Ctl, nextPos: number) {
  c.pos = Math.min(c.maxPos, Math.max(0, nextPos));
  const domIndex = itemIndexForPos(c, c.pos);
  c.track.style.transition = EASE;
  c.track.style.transform = `translate3d(${offsetFor(c.items, domIndex)}px,0,0)`;
  c.list.setAttribute("data-carousel-index", String(c.pos));
  // active-item flags for the visible window (faithful to the lib's item state).
  c.items.forEach((li, i) => {
    const active = i >= domIndex && i < domIndex + c.slidesToShow;
    li.classList.toggle("react-multi-carousel-item--active", active);
    li.setAttribute("aria-hidden", active ? "false" : "true");
  });
  // active dot.
  if (c.dots.length) {
    const ad = activeDotForPos(c, c.pos);
    c.dots.forEach((d, i) => d.classList.toggle("react-multi-carousel-dot--active", i === ad));
  }
  syncArrows(c);
}

// RW-16 (S10) — CONDITIONAL ARROW EDGE-STATES. §0f live-derived (production, read-only 2026-07-22): on a
// FINITE strip react-multi-carousel does not render the boundary arrow at all — at the first slide the LEFT
// arrow is ABSENT from the DOM, it appears after the first right-advance, and at the last slide the RIGHT
// arrow is likewise absent (symmetric). INFINITE (clone) strips wrap, so both arrows always show — excluded.
// Pixel-safety: this runs only from apply() (i.e. on interaction). At rest the served DOM is the untouched
// capture, which is itself a photocopy of live at that position and therefore already in the correct edge
// state — so the at-rest pixel gate is untouched by construction.
function syncArrows(c: Ctl) {
  if (c.infinite) return; // wrap-around: live keeps both arrows
  if (c.arrowL) c.arrowL.style.display = c.pos <= 0 ? "none" : "";
  if (c.arrowR) c.arrowR.style.display = c.pos >= c.maxPos ? "none" : "";
}

export default function CarouselLayer() {
  useEffect(() => {
    const cleanups: (() => void)[] = [];
    const autoplayOn =
      (window as unknown as { __carouselAutoplay?: boolean }).__carouselAutoplay === true;

    for (const list of Array.from(
      document.querySelectorAll<HTMLElement>(".react-multi-carousel-list"),
    )) {
      const track = list.querySelector<HTMLElement>(".react-multi-carousel-track");
      if (!track) continue;
      const items = Array.from(
        track.querySelectorAll<HTMLElement>(":scope > .react-multi-carousel-item"),
      );
      if (items.length === 0) continue;

      let arrowL = list.querySelector<HTMLElement>(".react-multiple-carousel__arrow--left");
      const arrowR = list.querySelector<HTMLElement>(".react-multiple-carousel__arrow--right");
      // RW-16 (S10) — the served capture is a photocopy of live AT THE FIRST SLIDE, and live renders NO left
      // arrow there, so the element simply does not exist in the mirror. To reproduce live's "left arrow
      // appears once you advance", mint one by cloning the right arrow and mirroring it. It is inserted
      // `display:none` (at-rest DOM/pixels unchanged); syncArrows() reveals it exactly when live would.
      // Cloning (rather than hand-authoring) keeps the lib's own classes/markup, so it styles identically.
      if (!arrowL && arrowR) {
        const mint = arrowR.cloneNode(true) as HTMLElement;
        mint.classList.remove("react-multiple-carousel__arrow--right");
        mint.classList.add("react-multiple-carousel__arrow--left");
        mint.setAttribute("aria-label", "Go to previous slide");
        mint.setAttribute("data-carousel-minted", "left");
        mint.style.display = "none";
        arrowR.parentElement?.appendChild(mint);
        arrowL = mint;
      }
      // dots live inside the list (default) or in the list's parent / next sibling (renderDotsOutside).
      const dotList =
        list.querySelector<HTMLElement>(".react-multi-carousel-dot-list") ??
        list.parentElement?.querySelector<HTMLElement>(":scope > .react-multi-carousel-dot-list") ??
        (list.nextElementSibling?.matches?.(".react-multi-carousel-dot-list")
          ? (list.nextElementSibling as HTMLElement)
          : null);
      const dotLis = dotList
        ? Array.from(dotList.querySelectorAll<HTMLElement>("li.react-multi-carousel-dot"))
        : [];

      // ── S35 · F236 — THE AUTOPLAY-MARQUEE SHAPE, now built ─────────────────────────────────────
      // §0f's third captured shape: no arrows, no dots, captured MID-ROTATION. The sponsor track was
      // frozen at transform=none with its first logo parked at x=-2248 (off-screen left), so the strip
      // read as a static row of whichever logos happened to sit in frame. Nothing was missing — 18
      // <img>, 6 distinct logos ×3, a duplicated marquee track — the logos were simply parked.
      // Restored as a translate loop over the SERVED track: no clone, no new dependency, and no src is
      // ever swapped (a marquee translates a fixed track; the F236 note above records that asserting
      // the src list changes is the trap that produced a wrong measurement first time round).
      // The PERIOD is derived, not chosen: one repetition's width, from distinct-logo count vs item
      // count. A track that is not actually duplicated yields repeats=1 and is left alone rather than
      // animated on a guess.
      if (!arrowR && dotLis.length === 0 && items.length >= 4) {
        const srcs = items.map((it) => it.querySelector("img")?.getAttribute("src") || "");
        const distinct = new Set(srcs.filter(Boolean)).size;
        const repeats = distinct > 0 ? Math.round(items.length / distinct) : 1;
        const period =
          repeats > 1
            ? items.slice(0, Math.round(items.length / repeats)).reduce((w, it) => w + it.offsetWidth, 0)
            : 0;
        // honour the OS setting — an unstoppable marquee is an accessibility defect, and a user who
        // asked for less motion asked for this too
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
        if (period > 0 && !reduced) {
          track.setAttribute("data-carousel-marquee", String(Math.round(period)));
          let x = 0;
          let last = 0;
          let raf = 0;
          const step = (t: number) => {
            if (last) {
              x -= (MARQUEE_PX_PER_S * (t - last)) / 1000;
              if (x <= -period) x += period; // seamless wrap: one period on looks identical
              track.style.transform = `translate3d(${x}px, 0, 0)`;
            }
            last = t;
            raf = window.requestAnimationFrame(step);
          };
          raf = window.requestAnimationFrame(step);
          cleanups.push(() => window.cancelAnimationFrame(raf));
          continue; // a marquee has no arrows or dots to wire
        }
      }

      // `||` (not `??`): a zero offsetWidth (unrendered/lazy item) must fall through to 1 to avoid /0.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const itemW = items[0]?.offsetWidth || 1;
      const slidesToShow = Math.max(1, Math.round(list.clientWidth / itemW));
      const capturedX = parseTranslateX(track.style.transform);
      const activeIdx = items.findIndex(
        (it) =>
          it.classList.contains("react-multi-carousel-item--active") ||
          it.getAttribute("aria-hidden") === "false",
      );

      // INFINITE (clone) detection: react-multi-carousel infinite mode renders symmetric clones around the
      // real slides and starts offset past the lead-clones. Signature (vs a finite card carousel captured at
      // rest): dots present AND the captured active item is past index 0 AND there are more DOM items than
      // dots. Then the real slides are the middle `dots.length` block; lead-clones = (items − dots) / 2.
      const infinite =
        dotLis.length >= 2 && activeIdx > 0 && items.length > dotLis.length;
      const realStart = infinite ? Math.round((items.length - dotLis.length) / 2) : 0;
      const maxPos = infinite
        ? dotLis.length - 1
        : Math.max(0, items.length - slidesToShow);
      // seed the logical position from the captured frame (pixel-neutral: we do NOT re-apply at rest).
      const seed = infinite
        ? Math.min(maxPos, Math.max(0, activeIdx - realStart))
        : Math.min(maxPos, Math.max(0, nearestItem(items, capturedX)));

      const c: Ctl = {
        list,
        track,
        items,
        dots: dotLis,
        infinite,
        realStart,
        slidesToShow,
        maxPos,
        pos: seed,
        arrowL,
        arrowR,
      };

      list.setAttribute("data-behavior", "carousel");
      list.setAttribute("data-carousel-index", String(seed));

      if (arrowR) {
        arrowR.style.cursor = "pointer";
        const onR = (e: Event) => {
          e.preventDefault();
          apply(c, c.pos + 1);
        };
        arrowR.addEventListener("click", onR);
        cleanups.push(() => arrowR.removeEventListener("click", onR));
      }
      if (arrowL) {
        arrowL.style.cursor = "pointer";
        const onL = (e: Event) => {
          e.preventDefault();
          apply(c, c.pos - 1);
        };
        arrowL.addEventListener("click", onL);
        cleanups.push(() => arrowL.removeEventListener("click", onL));
      }
      dotLis.forEach((dot, di) => {
        dot.style.cursor = "pointer";
        const btn = dot.querySelector<HTMLElement>("button") ?? dot;
        const onDot = (e: Event) => {
          e.preventDefault();
          apply(c, posForDot(c, di));
        };
        btn.addEventListener("click", onDot);
        cleanups.push(() => btn.removeEventListener("click", onDot));
      });

      // ---- swipe / drag (pointer) — react-multi-carousel is draggable+swipeable by default. Snap to the
      // nearest logical position on release. Only meaningful when there is more than one position.
      if (c.maxPos > 0) {
        let startX = 0;
        let dragging = false;
        let baseOffset = 0;
        const down = (e: PointerEvent) => {
          if ((e.target as HTMLElement)?.closest("a,button")) return; // don't hijack slide anchors
          dragging = true;
          startX = e.clientX;
          baseOffset = offsetFor(items, itemIndexForPos(c, c.pos));
          track.style.transition = "none";
        };
        const move = (e: PointerEvent) => {
          if (!dragging) return;
          track.style.transform = `translate3d(${baseOffset + (e.clientX - startX)}px,0,0)`;
        };
        const up = (e: PointerEvent) => {
          if (!dragging) return;
          dragging = false;
          const delta = Math.round(-(e.clientX - startX) / itemW);
          apply(c, c.pos + delta);
        };
        track.addEventListener("pointerdown", down);
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        cleanups.push(() => {
          track.removeEventListener("pointerdown", down);
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
        });
      }

      // ---- auto-play — HELD by default (gate determinism). Opt-in behind the harness flag; advances (wrapping
      // through the real slides — CLAMPED, never into clones) on an interval only when explicitly enabled.
      if (autoplayOn && c.maxPos > 0) {
        const id = window.setInterval(() => {
          apply(c, c.pos >= c.maxPos ? 0 : c.pos + 1);
        }, 3000);
        cleanups.push(() => window.clearInterval(id));
      }
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return <span data-carousel-layer hidden />;
}
